import { useEffect, useState } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import { getSmallAssetPath } from '@aytm/lib/common/assets'
import {
  EcommerceBannerPlacement,
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { readDimensions } from '../../utils/familyAxes.ts'
import ProductCard from './ProductCard.tsx'
import useCarouselNav from './useCarouselNav.ts'
import style from './Cart.module.scss'
import accentPickupIcon from '../assets/icons/accent-pickup.svg'
import accentCouponsIcon from '../assets/icons/accent-coupons.svg'
import accentCartIcon from '../assets/icons/accent-cart.svg'

interface ICartProps {
  response: EcommerceResponse
  schema: IEcommerceQuestionSchema
  totals: number[]
  onCartClose: () => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
  onDeleteFromCart: (index: number) => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onUpdateResponse: (response: EcommerceResponse) => void
  checkout: () => void
  scrollTop: () => void
}

const Cart = ({
  response,
  schema,
  totals,
  onCartClose,
  onAddToCart,
  onDeleteFromCart,
  onItemSelect,
  onUpdateResponse,
  checkout,
  scrollTop
}: ICartProps) => {
  const { t, languageTag } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()
  const [showAll, setShowAll] = useState<boolean>(false)
  const na = schema.answers.find(answer => answer.na)

  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const hasDiscount = (answer: IEcommerceAnswerSchema) =>
    Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  const sizeOf = (answer: IEcommerceAnswerSchema) => readDimensions(answer)[0]?.value

  // Cart lines = every answer currently in the cart, variants included (a variant is added
  // from its own product page). "Forgetting Something" = featured top-level products not yet
  // in the cart — carousels are driven by `featured`, the same flag as both "You May Also Like" rows.
  const cartItems = schema.answers.filter(answer => !answer.na && response.answers[answer.index].v > 0)
  const forgetting = schema.answers.filter(
    answer => answer.featured && !answer.na && !answer.parent_product && !response.answers[answer.index].v
  )
  const { trackRef, nav, measureNav, slide } = useCarouselNav(forgetting.length)

  // Subtotal is the pre-discount sum; savings is what the discounts remove; the estimated
  // total is the money total (post-discount) the shell already computes.
  const itemCount = totals[0]
  const estimatedTotal = totals[1]
  const subtotal = schema.answers.reduce((sum, a) => sum + (a.na ? 0 : response.answers[a.index].v * a.price), 0)
  const savings = Math.max(0, subtotal - estimatedTotal)
  const couponCount = cartItems.filter(hasDiscount).length

  // Cart caps are advisory while shopping (adding stays free) and only bite at checkout — the
  // shared behaviour of every other retailer. The message carries a price mask, so it is markup.
  const valid =
    (schema.max_money_spent ? estimatedTotal <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? itemCount <= schema.max_cart_size : true)
  const cartError = [
    schema.max_money_spent && estimatedTotal > schema.max_money_spent
      ? t<(max: string) => string>('questions', 'ec', 'maxSpentError')(price(schema.max_money_spent))
      : '',
    schema.max_cart_size && itemCount > schema.max_cart_size
      ? t<(max: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size)
      : ''
  ]
    .filter(Boolean)
    .join('')

  // "None of these" zeroes every other answer; the shell's na effect then finishes the question.
  const onNaSelect = () => {
    if (!na) return
    timeline.log({ type: 'na_select', ref: na.id })
    response.answers = response.answers.map(answer => ({ ...answer, v: answer.id === na.id ? 1 : 0 }))
    onUpdateResponse(response)
  }

  const thumb = (answer: IEcommerceAnswerSchema) =>
    answer.image ? (
      <span
        className={style.thumbImg}
        role="img"
        aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
        style={{ backgroundImage: `url('${getSmallAssetPath(answer.image)}')` }}
      />
    ) : (
      <span className={style.thumbNoImg} />
    )

  const priceBlock = (answer: IEcommerceAnswerSchema) =>
    hasDiscount(answer) ? (
      <>
        <span className={style.priceSale} dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }} />
        <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
      </>
    ) : (
      <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
    )

  const couponChip = (answer: IEcommerceAnswerSchema) => (
    <div className={style.couponChip}>
      <EmptyLink color="#1860ae">
        <div className={style.couponCheck} />
      </EmptyLink>
      <div className={style.couponText}>
        <span
          dangerouslySetInnerHTML={{
            __html: t<(amount: string) => string>(
              'questions',
              'ec',
              'kroger',
              'couponSave'
            )(price(answer.price - answer.discounted_price))
          }}
        />
        <EmptyLink color="#1860ae">
          <span className={style.couponLink}>{t('questions', 'ec', 'kroger', 'viewOffer')}</span>
        </EmptyLink>
      </div>
    </div>
  )

  const backupFooter = (
    <div className={style.lineFooter}>
      <span className={style.backupLabel}>
        <strong>{t('questions', 'ec', 'kroger', 'backupLabel')}</strong>{' '}
        {t('questions', 'ec', 'kroger', 'backupBestAvailable')}
      </span>
      <EmptyLink color="#1860ae">
        <span className={style.backupLink}>{t('questions', 'ec', 'kroger', 'chooseBackupItem')}</span>
      </EmptyLink>
    </div>
  )

  const stepper = (answer: IEcommerceAnswerSchema, qty: number) => (
    <div className={style.stepper}>
      <button
        type="button"
        className={style.stepMinus}
        onClick={() => onAddToCart(answer.index, -1)}
        aria-label={t('questions', 'ec', 'kroger', 1 === qty ? 'remove' : 'decreaseQuantity')}
      >
        <i className="pi pi-minus" />
      </button>
      <span className={style.stepQty}>
        {qty}
        {viewport.mobile && <span className={style.stepQtyLabel}>{t('questions', 'ec', 'kroger', 'inCart')}</span>}
      </span>
      <button
        type="button"
        className={style.stepPlus}
        onClick={() => onAddToCart(answer.index, 1)}
        disabled={schema.max_qty_per_product > 0 && qty >= schema.max_qty_per_product}
        aria-label={t('questions', 'ec', 'kroger', 'increaseQuantity')}
      >
        <i className="pi pi-plus" />
      </button>
    </div>
  )

  const renderLine = (answer: IEcommerceAnswerSchema) => {
    const qty = response.answers[answer.index].v
    const size = sizeOf(answer)
    return (
      <div className={style.line} key={answer.id} data-testid="SW-EC-CartItem">
        <div className={style.lineBody}>
          <div className={style.lineThumb} onClick={() => onItemSelect(answer.index)}>
            {thumb(answer)}
          </div>
          <div className={style.lineInfo}>
            <span
              className={style.lineTitle}
              onClick={() => onItemSelect(answer.index)}
              dangerouslySetInnerHTML={{ __html: answer.text }}
            />
            {size && <span className={style.lineSize}>{size}</span>}
          </div>
          <div className={style.lineQty}>
            {stepper(answer, qty)}
            <EmptyLink color="#1860ae">
              <span className={style.saveForLater}>{t('questions', 'ec', 'kroger', 'saveForLater')}</span>
            </EmptyLink>
          </div>
          <div className={style.linePrice}>
            {priceBlock(answer)}
            {hasDiscount(answer) && couponChip(answer)}
          </div>
        </div>
        {backupFooter}
      </div>
    )
  }

  // Mobile line is its own card (design/cart-mobile.png): price above the title, a dedicated
  // Remove control, and the Save-for-Later / stepper pair on their own row below.
  const renderMobileLine = (answer: IEcommerceAnswerSchema) => {
    const qty = response.answers[answer.index].v
    const size = sizeOf(answer)
    return (
      <div className={style.line} key={answer.id} data-testid="SW-EC-CartItem">
        <div className={style.lineBody}>
          <div className={style.lineThumb} onClick={() => onItemSelect(answer.index)}>
            {thumb(answer)}
          </div>
          <div className={style.lineInfo}>
            <div className={style.linePrice}>{priceBlock(answer)}</div>
            <span
              className={style.lineTitle}
              onClick={() => onItemSelect(answer.index)}
              dangerouslySetInnerHTML={{ __html: answer.text }}
            />
            {size && <span className={style.lineSize}>{size}</span>}
            {hasDiscount(answer) && couponChip(answer)}
          </div>
          <button
            type="button"
            className={style.removeButton}
            onClick={() => onDeleteFromCart(answer.index)}
            aria-label={t('questions', 'ec', 'kroger', 'remove')}
          >
            <i className="pi pi-times" />
          </button>
        </div>
        <div className={style.lineActions}>
          <EmptyLink color="#1860ae">
            <span className={style.saveForLater}>{t('questions', 'ec', 'kroger', 'saveForLater')}</span>
          </EmptyLink>
          {stepper(answer, qty)}
        </div>
        {backupFooter}
      </div>
    )
  }

  return (
    <section className={`${style.cart} ${viewport.mobile ? style.mobile : ''}`} data-testid="SW-Kroger-Cart">
      <div className={style.inner}>
        <nav className={style.breadcrumbs}>
          <span className={style.crumbLink} onClick={onCartClose}>
            {t('questions', 'ec', 'kroger', 'home')}
          </span>
          <i className="pi pi-chevron-right" />
          <span className={style.crumbCurrent}>{t('questions', 'ec', 'kroger', 'shoppingCart')}</span>
        </nav>
        <h1 className={style.title}>{t('questions', 'ec', 'kroger', 'yourCart')}</h1>

        {0 === cartItems.length ? (
          <div className={style.emptyCard}>
            <img src={accentCartIcon} alt="" role="presentation" className={style.emptyIcon} />
            <h2 className={style.emptyTitle}>{t('questions', 'ec', 'kroger', 'emptyTitle')}</h2>
            <p className={style.emptyText}>{t('questions', 'ec', 'kroger', 'emptyText')}</p>
            <button type="button" className={style.emptyAction} onClick={onCartClose}>
              {t('questions', 'ec', 'kroger', 'startYourOrder')}
              <i className="pi pi-arrow-right" />
            </button>
            {/* The only place the respondent can pick "None of these" — mirrors Costco / Chewy. */}
            {na && (
              <button type="button" className={style.naButton} onClick={onNaSelect}>
                {na.text || t('questions', 'ec', 'na')}
              </button>
            )}
          </div>
        ) : (
          <div className={style.columns}>
            <div className={style.main}>
              {/* The app drops the fulfilment card: lines are standalone cards, always expanded,
                  and "Change to Delivery" moves next to the summary's Pickup Details heading. */}
              {viewport.mobile ? (
                <div>{cartItems.map(renderMobileLine)}</div>
              ) : (
                <div className={style.pickupCard}>
                  <div className={style.pickupHeader}>
                    <img src={accentPickupIcon} alt="" role="presentation" className={style.pickupIcon} />
                    <span className={style.pickupTitle}>{t('questions', 'ec', 'kroger', 'fulfilmentPickup')}</span>
                    <EmptyLink color="#1860ae">
                      <span className={style.changeFulfillment}>
                        {t('questions', 'ec', 'kroger', 'changeToDelivery')}
                      </span>
                    </EmptyLink>
                  </div>

                  {showAll ? (
                    <>
                      <label className={style.substitutions}>
                        <EmptyLink color="#1860ae">
                          <span className={style.checkbox} />
                        </EmptyLink>
                        {t('questions', 'ec', 'kroger', 'allowSubstitutions')}
                      </label>
                      <div>{cartItems.map(renderLine)}</div>
                      <button type="button" className={style.toggleItems} onClick={() => setShowAll(false)}>
                        {t('questions', 'ec', 'kroger', 'showLess')}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className={style.preview}>
                        {cartItems.map(answer => (
                          <span className={style.previewThumb} key={answer.id}>
                            {thumb(answer)}
                          </span>
                        ))}
                      </div>
                      <button type="button" className={style.toggleItems} onClick={() => setShowAll(true)}>
                        {t('questions', 'ec', 'kroger', 'showAllItems')}
                      </button>
                    </>
                  )}
                </div>
              )}

              {forgetting.length > 0 && (
                <div className={style.forgetting} data-testid="SW-EC-InspiredByYourCart">
                  <h2 className={style.forgettingTitle}>{t('questions', 'ec', 'kroger', 'forgettingSomething')}</h2>
                  <div className={style.tabs}>
                    <span className={`${style.tab} ${style.tabActive}`}>
                      {t('questions', 'ec', 'kroger', 'tabPastItems')}
                    </span>
                    <EmptyLink color="#1860ae">
                      <span className={style.tab}>{t('questions', 'ec', 'kroger', 'tabSeasonalFavorites')}</span>
                    </EmptyLink>
                  </div>
                  <div className={style.forgettingViewport}>
                    {nav.overflowing && !viewport.mobile && (
                      <>
                        <button
                          type="button"
                          className={`${style.navBtn} ${style.navBtnLeft}`}
                          disabled={nav.atStart}
                          onClick={() => slide(-1)}
                          aria-label={t('questions', 'ec', 'kroger', 'previous')}
                        >
                          <i className="pi pi-chevron-left" />
                        </button>
                        <button
                          type="button"
                          className={`${style.navBtn} ${style.navBtnRight}`}
                          disabled={nav.atEnd}
                          onClick={() => slide(1)}
                          aria-label={t('questions', 'ec', 'kroger', 'next')}
                        >
                          <i className="pi pi-chevron-right" />
                        </button>
                      </>
                    )}
                    <div ref={trackRef} className={style.forgettingRow} onScroll={measureNav}>
                      {forgetting.map(answer => (
                        <div className={style.forgettingCard} key={answer.id}>
                          <ProductCard
                            data-testid="SW-EC-CarouselItem"
                            schema={schema}
                            answer={answer}
                            amount={response.answers[answer.index].v}
                            findability={false}
                            compact
                            disabled={'focus' == schema.mode_type && !answer.focused}
                            bannerPlacement="featured"
                            onSelect={() => onItemSelect(answer.index, 'featured')}
                            onAdd={qnt => onAddToCart(answer.index, qnt, true)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <aside className={style.aside}>
              <div className={style.panel}>
                {!viewport.mobile && (
                  <h2 className={style.panelTitle}>{t('questions', 'ec', 'kroger', 'pickupDetails')}</h2>
                )}

                {couponCount > 0 && (
                  <>
                    {viewport.mobile && (
                      <h2 className={style.panelTitle}>{t('questions', 'ec', 'kroger', 'rewardsSavings')}</h2>
                    )}
                    <div className={style.savingsCard}>
                      <img src={accentCouponsIcon} alt="" role="presentation" className={style.savingsIcon} />
                      <div className={style.savingsText}>
                        <span className={style.savingsHeadline}>
                          {t('questions', 'ec', 'kroger', 'dontMissSavings')}
                        </span>
                        <EmptyLink color="#1860ae">
                          <span className={style.savingsLink}>
                            {t<(count: number) => string>('questions', 'ec', 'kroger', 'viewCoupons')(couponCount)}
                          </span>
                        </EmptyLink>
                      </div>
                    </div>
                  </>
                )}

                {viewport.mobile && (
                  <div className={style.pickupDetails}>
                    <h2 className={style.panelTitle}>{t('questions', 'ec', 'kroger', 'pickupDetails')}</h2>
                    <EmptyLink color="#1860ae">
                      <span className={style.changeDelivery}>{t('questions', 'ec', 'kroger', 'changeToDelivery')}</span>
                    </EmptyLink>
                  </div>
                )}

                <div className={style.summary}>
                  <div className={style.summaryTitle}>{t('questions', 'ec', 'kroger', 'paymentSummary')}</div>
                  <div className={style.summaryRow}>
                    <span>{t<(count: number) => string>('questions', 'ec', 'kroger', 'subtotal')(itemCount)}</span>
                    <span dangerouslySetInnerHTML={{ __html: price(subtotal) }} />
                  </div>
                  {savings > 0 && (
                    <div className={style.summaryRow}>
                      <span>{t('questions', 'ec', 'kroger', 'krogerSavings')}</span>
                      <span className={style.savingsValue}>
                        &minus;
                        <span dangerouslySetInnerHTML={{ __html: price(savings) }} />
                      </span>
                    </div>
                  )}
                  <div className={style.summaryRow}>
                    <span>{t('questions', 'ec', 'kroger', 'taxesAndFees')}</span>
                    <span className={style.tbd}>{t('questions', 'ec', 'kroger', 'tbd')}</span>
                  </div>
                  <div className={style.summaryDivider} />
                  <div className={`${style.summaryRow} ${style.totalRow}`}>
                    <span>{t('questions', 'ec', 'kroger', 'estimatedTotalPlain')}</span>
                    <span dangerouslySetInnerHTML={{ __html: price(estimatedTotal) }} />
                  </div>
                </div>

                <div className={style.location}>
                  <div className={style.locationHeader}>
                    <span className={style.summaryTitle}>{t('questions', 'ec', 'kroger', 'pickupLocation')}</span>
                    <EmptyLink color="#1860ae">
                      <span className={style.changeStore}>{t('questions', 'ec', 'kroger', 'changeStore')}</span>
                    </EmptyLink>
                  </div>
                  <div className={style.locationLine}>{t('questions', 'ec', 'kroger', 'locationStore')}</div>
                  <div className={style.locationLine}>{t('questions', 'ec', 'kroger', 'locationNearYou')}</div>
                  <div className={style.locationNote}>{t('questions', 'ec', 'kroger', 'locationNote')}</div>
                </div>

                {!valid && <div className={style.cartError} dangerouslySetInnerHTML={{ __html: cartError }} />}

                {/* The empty cart renders its own branch, so only a broken cap can disable checkout here.
                    On mobile the sticky bar is the only CTA — never a duplicate pair on screen. */}
                {!viewport.mobile && (
                  <button type="button" className={style.checkout} onClick={checkout} disabled={!valid}>
                    {t('questions', 'ec', 'kroger', 'continueToCheckout')}
                  </button>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>

      {viewport.mobile && cartItems.length > 0 && (
        <div className={style.checkoutMobile}>
          <button type="button" className={style.checkout} onClick={checkout} disabled={!valid}>
            {t('questions', 'ec', 'kroger', 'continueToCheckout')}
          </button>
        </div>
      )}
    </section>
  )
}

export default Cart
